import { useCallback, useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Divider, P, Wrapper } from '@farewill/ui'
import { BORDER, COLOR, GTR } from '@farewill/ui/tokens'
import { getWidthOfContainerColumns } from '@farewill/ui/helpers/layout'
import { screenMax, screenMin } from '@farewill/ui/helpers/responsive'

import useApi from 'lib/effects/api'
import useSaveProbateEstateField from 'lib/effects/save-probate-estate-field'
import useSaveProbateCaseField from 'lib/probate/save-probate-case-field'
import {
  Context,
  withProvider,
} from 'lib/context/probate-estate-request-status'
import RequestStatus from 'components/request-status'
import Sticky from 'routes/lead/sticky-section'
import { ProbateEstate } from 'lib/models/probate-estate'
import { ProbateCase } from 'lib/models/probate-case'

import EstateSummaryHeader from './components/estate-summary-header'
import useEstateQuestionnaireStatus from './hooks/use-estate-questionnaire-status'
import People from './people'
import Estate from './estate'
import DebtsAndLiabilities from './debts-and-liabilities'
import LegalAndFinancial from './legal-and-financial'
import SectionNav from './components/section-nav'
import { MAX_CONTENT_WIDTH } from './constants'
import { EstatePeopleProvider } from './shared-contacts/estate-people'

// mirrors ui/src/routes/lead/quote-form.tsx DisableableForm
const DisableableSections = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
    `}
`

// mirrors the collapsed tasks panel's width in components/tasks-panel/sticky-wrapper.js;
// in the side column the grid keeps it narrower than this
const SECTION_NAV_MAX_WIDTH = getWidthOfContainerColumns(4, 'L')

// the side column needs the full-width container, which starts at xl (1296px);
// below that the nav overlays the form instead
const StyledLayout = styled.div`
  display: grid;
  gap: ${GTR.L};

  ${screenMax.l`
    grid-template-areas: 'content';
  `}

  ${screenMin.xl`
    grid-template-areas: 'content nav';
    grid-template-columns: minmax(0, ${MAX_CONTENT_WIDTH}) 1fr;
  `}
`

// no backdrop: only the panel itself takes clicks, so the form stays usable
// around it. Shadow mirrors StyledStickyWrapper in
// components/tasks-panel/sticky-wrapper.js
const StyledNavArea = styled.div`
  padding: ${GTR.M};

  ${screenMax.l`
    position: fixed;
    top: ${GTR.L};
    right: ${GTR.S};
    z-index: 2;
    width: max-content;
    max-width: min(${SECTION_NAV_MAX_WIDTH}px, calc(100vw - ${
      parseInt(GTR.S) * 2
    }px));
    max-height: calc(100vh - ${parseInt(GTR.L) * 2}px);
    overflow-y: auto;
    background-color: ${COLOR.WHITE};
    box-shadow: ${BORDER.SHADOW.M};
  `}

  ${screenMin.xl`
    grid-area: nav;
  `}
`

const StyledContentArea = styled.div`
  grid-area: content;
  min-width: 0;
`

type EstateQuestionnaireProps = {
  probateEstateId?: number
  probateCase?: ProbateCase
}

type LoadedQuestionnaireProps = {
  probateEstateId: number
  probateCase: ProbateCase
  probateEstate: ProbateEstate
  errors: string[]
  isLoading?: boolean
  onProbateEstateUpdate: () => void
  saveProbateEstateField: ReturnType<
    typeof useSaveProbateEstateField
  >['saveProbateEstateField']
  saveProbateCaseField: ReturnType<
    typeof useSaveProbateCaseField
  >['saveProbateCaseField']
}

const LoadedQuestionnaire = ({
  probateEstateId,
  probateCase,
  probateEstate,
  errors,
  isLoading,
  onProbateEstateUpdate,
  saveProbateEstateField,
  saveProbateCaseField,
}: LoadedQuestionnaireProps) => {
  const sendToExizent = useEstateQuestionnaireStatus({
    probateCaseId: probateCase.id,
    externalExizentLastSentAt:
      probateEstate.attributes.externalExizentLastSentAt,
    onProbateEstateUpdate,
  })

  return (
    <EstatePeopleProvider probateEstateId={probateEstateId}>
      <EstateSummaryHeader
        probateCase={probateCase}
        probateEstate={probateEstate}
        sendToExizent={sendToExizent}
      />
      {!sendToExizent.isReadOnly && (
        <RequestStatus apiErrors={errors} isSaving={isLoading} />
      )}
      <DisableableSections
        $disabled={sendToExizent.isReadOnly}
        data-testid="estate-questionnaire-form"
        data-disabled={sendToExizent.isReadOnly ? 'true' : 'false'}
      >
        <People
          probateEstate={probateEstate}
          probateCase={probateCase}
          saveProbateEstateField={saveProbateEstateField}
          saveProbateCaseField={saveProbateCaseField}
        />
        <Divider margin={['XL', 0]} data-testid="section-divider" />
        <Estate
          probateEstate={probateEstate}
          saveProbateEstateField={saveProbateEstateField}
        />
        <Divider margin={['XL', 0]} data-testid="section-divider" />
        <DebtsAndLiabilities
          probateEstate={probateEstate}
          saveProbateEstateField={saveProbateEstateField}
        />
        <Divider margin={['XL', 0]} />
        <LegalAndFinancial
          probateEstate={probateEstate}
          saveProbateEstateField={saveProbateEstateField}
        />
      </DisableableSections>
    </EstatePeopleProvider>
  )
}

const EstateQuestionnaire = ({
  probateEstateId,
  probateCase,
}: EstateQuestionnaireProps) => {
  const [probateEstate, setProbateEstate] = useState<ProbateEstate>()

  const [
    { data: fetchedProbateEstate, errors: fetchErrors },
    fetchProbateEstate,
  ] = useApi<ProbateEstate>()

  const { saveProbateEstateField, isEstateUpdating, estateErrors } =
    useSaveProbateEstateField({
      probateEstateId: probateEstateId as number,
    })

  const { saveProbateCaseField, isCaseUpdating, caseErrors } =
    useSaveProbateCaseField({
      probateCaseId: probateCase?.id as number,
    })

  useEffect(() => {
    if (probateEstateId)
      // avoids an unhandled promise rejection; errors state above covers this
      fetchProbateEstate({
        url: `/api/probate-estates/${probateEstateId}`,
      }).catch(() => undefined)
  }, [fetchProbateEstate, probateEstateId])

  useEffect(() => {
    setProbateEstate(fetchedProbateEstate ?? undefined)
  }, [fetchedProbateEstate])

  const refetchProbateEstate = useCallback(() => {
    if (!probateEstateId) return

    fetchProbateEstate({
      url: `/api/probate-estates/${probateEstateId}`,
    }).catch(() => undefined)
  }, [fetchProbateEstate, probateEstateId])

  const { errors, isLoading, setErrors, setIsLoading } = useContext(Context)

  useEffect(() => {
    setErrors?.([...fetchErrors, ...estateErrors, ...caseErrors])
  }, [setErrors, fetchErrors, estateErrors, caseErrors])

  useEffect(() => {
    setIsLoading?.(isEstateUpdating || isCaseUpdating)
  }, [setIsLoading, isEstateUpdating, isCaseUpdating])

  if (!probateEstateId)
    return (
      <Wrapper maxWidth={MAX_CONTENT_WIDTH}>
        <P size="L">No estate found for this case.</P>
        <RequestStatus apiErrors={errors} isSaving={isLoading} />
      </Wrapper>
    )

  if (!probateEstate || !probateCase)
    return (
      <Wrapper maxWidth={MAX_CONTENT_WIDTH}>
        <P size="L">Loading...</P>
        <RequestStatus apiErrors={errors} isSaving={isLoading} />
      </Wrapper>
    )

  return (
    <StyledLayout>
      <StyledNavArea>
        <Sticky.Item maxWidth={SECTION_NAV_MAX_WIDTH}>
          <SectionNav />
        </Sticky.Item>
      </StyledNavArea>
      <StyledContentArea>
        <LoadedQuestionnaire
          probateEstateId={probateEstateId}
          probateCase={probateCase}
          probateEstate={probateEstate}
          errors={errors}
          isLoading={isLoading}
          onProbateEstateUpdate={refetchProbateEstate}
          saveProbateEstateField={saveProbateEstateField}
          saveProbateCaseField={saveProbateCaseField}
        />
      </StyledContentArea>
    </StyledLayout>
  )
}

export default withProvider(EstateQuestionnaire)
